/**
 * Pure CPU geometry prep for 3D view — safe to run in a Web Worker (no DOM / Three.js).
 */

import type { ElementType } from '../types';
export type { ElementType } from '../types';

export type CompactInstanceElement = {
  type: ElementType;
  x: number;
  y: number;
  z: number;
  rot: number;
  w: number;
  h: number;
  d: number;
  color?: string;
  railType?: string;
};

export type InstanceBatchResult = {
  key: string;
  elementType: ElementType;
  count: number;
  matrices: Float32Array;
  colors: Float32Array;
};

export type FloorLineBatchResult = {
  key: string;
  positions: Float32Array;
  color: string;
  opacity: number;
};

export type ZoneFillResult = {
  x: number;
  z: number;
  w: number;
  h: number;
  color: string;
  opacity: number;
  layer?: string;
};

/** Max floats per WebGL line batch (~500k segments). Large DXFs are split into chunks. */
const MAX_FLOATS_PER_LINE_BATCH = 3_000_000;
/** Hard cap on total floor line floats; excess is decimated for GPU stability. */
const MAX_TOTAL_FLOOR_FLOATS = 18_000_000;
const IMPORTED_DXF_FLOOR_OPACITY = 1;

function chunkLinePositions(
  key: string,
  positions: Float32Array,
  color: string,
  opacity: number
): FloorLineBatchResult[] {
  if (positions.length <= MAX_FLOATS_PER_LINE_BATCH) {
    return [{ key, positions, color, opacity }];
  }
  const batches: FloorLineBatchResult[] = [];
  for (let off = 0; off < positions.length; off += MAX_FLOATS_PER_LINE_BATCH) {
    batches.push({
      key: `${key}-${off / MAX_FLOATS_PER_LINE_BATCH}`,
      positions: positions.slice(off, Math.min(off + MAX_FLOATS_PER_LINE_BATCH, positions.length)),
      color,
      opacity,
    });
  }
  return batches;
}

/** Drop segments uniformly when a vertex buffer exceeds the GPU budget. */
function decimateLinePositions(positions: Float32Array, maxFloats: number): Float32Array {
  if (positions.length <= maxFloats) return positions;
  const segmentFloats = 6;
  const maxSegments = Math.floor(maxFloats / segmentFloats);
  const totalSegments = Math.floor(positions.length / segmentFloats);
  const stride = Math.max(1, Math.ceil(totalSegments / maxSegments));
  const out: number[] = [];
  for (let seg = 0; seg < totalSegments; seg += stride) {
    const i = seg * segmentFloats;
    for (let j = 0; j < segmentFloats; j++) out.push(positions[i + j]!);
  }
  return new Float32Array(out);
}

const FLOOR_LAYER_COLORS: Record<string, { color: string; opacity: number }> = {
  walls: { color: '#1e293b', opacity: 0.95 },
  grid: { color: '#475569', opacity: 0.35 },
  bays: { color: '#64748b', opacity: 0.5 },
  aisles: { color: '#fbbf24', opacity: 0.85 },
  'lane-fill': { color: '#f59e0b', opacity: 0.18 },
  'storage-zones': { color: '#115e59', opacity: 0.12 },
  'equipment-pads': { color: '#94a3b8', opacity: 0.55 },
};

const TYPE_DEFAULT_COLORS: Record<ElementType, string> = {
  stocker: '#CFC8BC',
  'oht-vehicle': '#F1F0EB',
  'overhead-rail': '#293A4C',
  'overhead-curve': '#293A4C',
  'cross-rail': '#475264',
  stb: '#2A3A5B',
  ohb: '#405A78',
  lifter: '#2A3A5B',
  'support-pillar': '#B8BDC1',
  'horizontal-beam': '#B8BDC1',
  'suspension-rod': '#A7B0B8',
  rectangle: '#C8C9C6',
  circle: '#E5E7E8',
  text: '#475264',
};

function isCurveRail(type: ElementType, railType?: string): boolean {
  return (
    (type === 'overhead-rail' || type === 'overhead-curve') &&
    (railType === 'curve90' || railType === 'curve45' || railType === 'curve180')
  );
}

/** Column-major T × R_y × S (matches Three.js Object3D compose). */
function composeMatrix(
  px: number,
  py: number,
  pz: number,
  rotY: number,
  sx: number,
  sy: number,
  sz: number,
  out: Float32Array,
  offset: number
): void {
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);

  out[offset] = sx * c;
  out[offset + 1] = 0;
  out[offset + 2] = sx * s;
  out[offset + 3] = 0;

  out[offset + 4] = 0;
  out[offset + 5] = sy;
  out[offset + 6] = 0;
  out[offset + 7] = 0;

  out[offset + 8] = -sz * s;
  out[offset + 9] = 0;
  out[offset + 10] = sz * c;
  out[offset + 11] = 0;

  out[offset + 12] = px;
  out[offset + 13] = py;
  out[offset + 14] = pz;
  out[offset + 15] = 1;
}

function parseColorToRgb(color: string): [number, number, number] {
  const hex = color.trim();
  if (hex.startsWith('#')) {
    let h = hex.slice(1);
    if (h.length === 3) {
      h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    }
    if (h.length >= 6) {
      const n = parseInt(h.slice(0, 6), 16);
      if (Number.isFinite(n)) {
        return [(n >> 16) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
      }
    }
  }
  const def = TYPE_DEFAULT_COLORS['oht-vehicle'];
  return parseColorToRgb(def);
}

function getFloorLayerStyle(layer: string): { color: string; opacity: number } {
  return FLOOR_LAYER_COLORS[layer] ?? { color: '#3b82f6', opacity: 0.55 };
}

function colorFromLayerName(layer: string): string {
  let hash = 0;
  for (let i = 0; i < layer.length; i++) {
    hash = (hash * 31 + layer.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 52%, 58%)`;
}

export function buildInstanceBatches(items: CompactInstanceElement[]): InstanceBatchResult[] {
  const groups = new Map<string, { type: ElementType; matrices: number[]; colors: number[] }>();

  for (const el of items) {
    if (isCurveRail(el.type, el.railType)) continue;

    const px = el.x + el.w / 2;
    const py = el.y + el.d / 2;
    const pz = el.z + el.h / 2;
    const rotY = (-el.rot * Math.PI) / 180;

    const key = `${el.type}|${Math.round(el.w)}|${Math.round(el.h)}|${Math.round(el.d)}`;
    let group = groups.get(key);
    if (!group) {
      group = { type: el.type, matrices: [], colors: [] };
      groups.set(key, group);
    }

    const m = new Float32Array(16);
    composeMatrix(px, py, pz, rotY, el.w, el.d, el.h, m, 0);
    for (let i = 0; i < 16; i++) group.matrices.push(m[i]!);

    const [r, g, b] = parseColorToRgb(el.color ?? TYPE_DEFAULT_COLORS[el.type]);
    group.colors.push(r, g, b);
  }

  const batches: InstanceBatchResult[] = [];
  for (const [key, group] of groups) {
    const count = group.colors.length / 3;
    if (count === 0) continue;
    batches.push({
      key,
      elementType: group.type,
      count,
      matrices: new Float32Array(group.matrices),
      colors: new Float32Array(group.colors),
    });
  }

  return batches;
}

export type FloorPolylineInput = {
  layer: string;
  vertices: number[];
};

function resolveLayerStrokeColor(
  layerName: string,
  layerIndex: number,
  layerStrokeColors: string[] | undefined
): string {
  const preset = getFloorLayerStyle(layerName);
  if (Object.prototype.hasOwnProperty.call(FLOOR_LAYER_COLORS, layerName)) {
    return preset.color;
  }
  if (layerStrokeColors && layerIndex >= 0 && layerIndex < layerStrokeColors.length) {
    return layerStrokeColors[layerIndex]!;
  }
  return colorFromLayerName(layerName);
}

function buildLayerLineBatchesFromVerts(
  layerVerts: Map<string, { verts: number[]; layerIndex: number }>,
  layerStrokeColors: string[] | undefined,
  opacity = IMPORTED_DXF_FLOOR_OPACITY
): FloorLineBatchResult[] {
  const batches: FloorLineBatchResult[] = [];
  let totalFloats = 0;
  for (const [, { verts }] of layerVerts) totalFloats += verts.length;

  const globalDecimate =
    totalFloats > MAX_TOTAL_FLOOR_FLOATS ? MAX_TOTAL_FLOOR_FLOATS / Math.max(totalFloats, 1) : 1;

  for (const [layer, { verts, layerIndex }] of layerVerts) {
    if (verts.length === 0) continue;
    let positions: Float32Array = new Float32Array(verts);
    const layerBudget = Math.max(6, Math.floor(positions.length * globalDecimate));
    if (positions.length > layerBudget) {
      positions = decimateLinePositions(positions, layerBudget);
    }
    const color = resolveLayerStrokeColor(layer, layerIndex, layerStrokeColors);
    batches.push(...chunkLinePositions(layer, positions, color, opacity));
  }
  return batches;
}

export function buildFloorLineBatchesIndexed(
  polylines: number[][],
  polylineLayers: number[],
  layerNames: string[],
  hiddenLayers: Set<string>,
  visible: boolean,
  _darkMode: boolean,
  layerStrokeColors?: string[],
  polylineStrokeColors?: string[]
): FloorLineBatchResult[] {
  if (!visible || polylines.length === 0) return [];

  const layerNameAt = (polylineIndex: number) =>
    layerNames[polylineLayers[polylineIndex] ?? 0] ?? '0';

  const layerVerts = new Map<string, { verts: number[]; layerIndex: number; layerName: string; color: string }>();
  for (let pi = 0; pi < polylines.length; pi++) {
    const layerIndex = polylineLayers[pi] ?? 0;
    const layer = layerNameAt(pi);
    if (hiddenLayers.has(layer)) continue;
    const pts = polylines[pi];
    if (!pts || pts.length < 4) continue;

    const fallbackColor = resolveLayerStrokeColor(layer, layerIndex, layerStrokeColors);
    const color = polylineStrokeColors?.[pi] ?? fallbackColor;
    const bucketKey = `${layer}__c__${color}`;

    let bucket = layerVerts.get(bucketKey);
    if (!bucket) {
      bucket = { verts: [], layerIndex, layerName: layer, color };
      layerVerts.set(bucketKey, bucket);
    }
    for (let i = 0; i < pts.length - 2; i += 2) {
      bucket.verts.push(pts[i]!, 0.5, pts[i + 1]!, pts[i + 2]!, 0.5, pts[i + 3]!);
    }
  }

  const batches: FloorLineBatchResult[] = [];
  let totalFloats = 0;
  for (const [, { verts }] of layerVerts) totalFloats += verts.length;

  const globalDecimate =
    totalFloats > MAX_TOTAL_FLOOR_FLOATS ? MAX_TOTAL_FLOOR_FLOATS / Math.max(totalFloats, 1) : 1;

  for (const [, { verts, layerName, color }] of layerVerts) {
    if (verts.length === 0) continue;
    let positions: Float32Array = new Float32Array(verts);
    const layerBudget = Math.max(6, Math.floor(positions.length * globalDecimate));
    if (positions.length > layerBudget) {
      positions = decimateLinePositions(positions, layerBudget);
    }
    batches.push(...chunkLinePositions(`${layerName}__c__${color}`, positions, color, IMPORTED_DXF_FLOOR_OPACITY));
  }

  return batches;
}

export function buildZoneFillsIndexed(
  polylines: number[][],
  polylineLayers: number[],
  layerNames: string[],
  hiddenLayers: Set<string>,
  visible: boolean
): ZoneFillResult[] {
  if (!visible) return [];

  const layerNameAt = (polylineIndex: number) =>
    layerNames[polylineLayers[polylineIndex] ?? 0] ?? '0';

  const items: ZoneFillResult[] = [];
  for (let pi = 0; pi < polylines.length; pi++) {
    const layer = layerNameAt(pi);
    if (layer !== 'lane-fill' && layer !== 'storage-zones') continue;
    if (hiddenLayers.has(layer)) continue;
    const pts = polylines[pi];
    if (!pts || pts.length < 8) continue;

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (let i = 0; i < pts.length; i += 2) {
      minX = Math.min(minX, pts[i]!);
      maxX = Math.max(maxX, pts[i]!);
      minY = Math.min(minY, pts[i + 1]!);
      maxY = Math.max(maxY, pts[i + 1]!);
    }

    const style = getFloorLayerStyle(layer);
    items.push({
      x: minX,
      z: minY,
      w: maxX - minX,
      h: maxY - minY,
      color: style.color,
      opacity: style.opacity,
      layer,
    });
  }
  return items;
}

export function buildFloorLineBatches(
  polylines: FloorPolylineInput[],
  hiddenLayers: Set<string>,
  visible: boolean,
  _darkMode: boolean,
  layerStrokeColors?: string[]
): FloorLineBatchResult[] {
  if (!visible || polylines.length === 0) return [];

  const layerVerts = new Map<string, { verts: number[]; layerIndex: number }>();
  const layerIndexByName = new Map<string, number>();
  let nextLayerIndex = 0;

  for (const pl of polylines) {
    if (hiddenLayers.has(pl.layer)) continue;
    const pts = pl.vertices;
    if (pts.length < 4) continue;

    let layerIndex = layerIndexByName.get(pl.layer);
    if (layerIndex === undefined) {
      layerIndex = nextLayerIndex++;
      layerIndexByName.set(pl.layer, layerIndex);
    }

    let bucket = layerVerts.get(pl.layer);
    if (!bucket) {
      bucket = { verts: [], layerIndex };
      layerVerts.set(pl.layer, bucket);
    }

    for (let i = 0; i < pts.length - 2; i += 2) {
      bucket.verts.push(pts[i]!, 0.5, pts[i + 1]!, pts[i + 2]!, 0.5, pts[i + 3]!);
    }
  }

  return buildLayerLineBatchesFromVerts(layerVerts, layerStrokeColors);
}

export function buildZoneFills(
  polylines: FloorPolylineInput[],
  hiddenLayers: Set<string>,
  visible: boolean
): ZoneFillResult[] {
  if (!visible) return [];

  const items: ZoneFillResult[] = [];

  for (const pl of polylines) {
    if (pl.layer !== 'lane-fill' && pl.layer !== 'storage-zones') continue;
    if (hiddenLayers.has(pl.layer)) continue;
    const pts = pl.vertices;
    if (pts.length < 8) continue;

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (let i = 0; i < pts.length; i += 2) {
      minX = Math.min(minX, pts[i]);
      maxX = Math.max(maxX, pts[i]);
      minY = Math.min(minY, pts[i + 1]);
      maxY = Math.max(maxY, pts[i + 1]);
    }

    const style = getFloorLayerStyle(pl.layer);
    items.push({
      x: minX,
      z: minY,
      w: maxX - minX,
      h: maxY - minY,
      color: style.color,
      opacity: style.opacity,
      layer: pl.layer,
    });
  }

  return items;
}

/** Minimum element count before offloading to a worker (avoids spawn overhead). */
export const WORKER_INSTANCE_THRESHOLD = 80;

/** Minimum polyline count before offloading floor prep to a worker. */
export const WORKER_FLOOR_THRESHOLD = 500;
